function normalizeFiniteNumber(value) {
  if (value == null || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function normalizeNonNegativeInteger(value) {
  const numericValue = normalizeFiniteNumber(value);
  return numericValue != null && numericValue >= 0 ? Math.round(numericValue) : null;
}

function normalizeOptionalText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function readPerformanceEntries(performanceObject, entryType) {
  if (!performanceObject || typeof performanceObject.getEntriesByType !== "function") {
    return [];
  }

  try {
    const entries = performanceObject.getEntriesByType(entryType);
    return Array.isArray(entries) ? entries : Array.from(entries || []);
  } catch {
    return [];
  }
}

function buildNavigationTimingSnapshot(performanceObject) {
  const navigationEntry = readPerformanceEntries(performanceObject, "navigation")[0];
  if (!navigationEntry || typeof navigationEntry !== "object") {
    return null;
  }

  return {
    decoded_body_size_bytes: normalizeNonNegativeInteger(navigationEntry.decodedBodySize),
    dom_complete_ms: normalizeFiniteNumber(navigationEntry.domComplete),
    dom_content_loaded_ms: normalizeFiniteNumber(navigationEntry.domContentLoadedEventEnd),
    dom_interactive_ms: normalizeFiniteNumber(navigationEntry.domInteractive),
    duration_ms: normalizeFiniteNumber(navigationEntry.duration),
    encoded_body_size_bytes: normalizeNonNegativeInteger(navigationEntry.encodedBodySize),
    load_event_end_ms: normalizeFiniteNumber(navigationEntry.loadEventEnd),
    redirect_count: normalizeNonNegativeInteger(navigationEntry.redirectCount),
    response_end_ms: normalizeFiniteNumber(navigationEntry.responseEnd),
    response_start_ms: normalizeFiniteNumber(navigationEntry.responseStart),
    transfer_size_bytes: normalizeNonNegativeInteger(navigationEntry.transferSize),
    type: normalizeOptionalText(navigationEntry.type),
  };
}

function buildPaintTimingSnapshot(performanceObject) {
  const paintEntries = readPerformanceEntries(performanceObject, "paint");
  if (!paintEntries.length) {
    return null;
  }

  return paintEntries.reduce((snapshot, entry) => {
    const entryName = normalizeOptionalText(entry?.name);
    if (entryName) {
      snapshot[entryName.replaceAll("-", "_")] = normalizeFiniteNumber(entry.startTime);
    }
    return snapshot;
  }, {});
}

function buildPerformanceMemorySnapshot(performanceObject) {
  const memory = performanceObject?.memory;
  if (!memory || typeof memory !== "object") {
    return null;
  }

  return {
    js_heap_size_limit_bytes: normalizeNonNegativeInteger(memory.jsHeapSizeLimit),
    total_js_heap_size_bytes: normalizeNonNegativeInteger(memory.totalJSHeapSize),
    used_js_heap_size_bytes: normalizeNonNegativeInteger(memory.usedJSHeapSize),
  };
}

function buildResourceTimingSnapshot(performanceObject) {
  const resourceEntries = readPerformanceEntries(performanceObject, "resource");
  const byInitiatorType = {};
  let decodedBodySizeBytes = 0;
  let encodedBodySizeBytes = 0;
  let maximumDurationMs = 0;
  let totalDurationMs = 0;
  let transferSizeBytes = 0;

  resourceEntries.forEach((entry) => {
    const initiatorType = normalizeOptionalText(entry?.initiatorType) || "other";
    byInitiatorType[initiatorType] = (byInitiatorType[initiatorType] || 0) + 1;

    const durationMs = normalizeFiniteNumber(entry?.duration) ?? 0;
    decodedBodySizeBytes += normalizeFiniteNumber(entry?.decodedBodySize) ?? 0;
    encodedBodySizeBytes += normalizeFiniteNumber(entry?.encodedBodySize) ?? 0;
    maximumDurationMs = Math.max(maximumDurationMs, durationMs);
    totalDurationMs += durationMs;
    transferSizeBytes += normalizeFiniteNumber(entry?.transferSize) ?? 0;
  });

  return {
    by_initiator_type: byInitiatorType,
    count: resourceEntries.length,
    decoded_body_size_bytes: Math.round(decodedBodySizeBytes),
    encoded_body_size_bytes: Math.round(encodedBodySizeBytes),
    maximum_duration_ms: maximumDurationMs,
    total_duration_ms: totalDurationMs,
    transfer_size_bytes: Math.round(transferSizeBytes),
  };
}

function buildLongTaskSnapshot(performanceObject) {
  const entries = readPerformanceEntries(performanceObject, "longtask");
  const durations = entries
    .map((entry) => normalizeFiniteNumber(entry?.duration))
    .filter((duration) => duration != null);

  return {
    count: entries.length,
    maximum_duration_ms: durations.length ? Math.max(...durations) : 0,
    total_duration_ms: durations.reduce((total, duration) => total + duration, 0),
  };
}

function buildEventCountSnapshot(performanceObject) {
  const eventCounts = performanceObject?.eventCounts;
  if (!eventCounts || typeof eventCounts.forEach !== "function") {
    return null;
  }

  const snapshot = {};
  try {
    eventCounts.forEach((value, key) => {
      const eventName = normalizeOptionalText(key);
      if (eventName) {
        snapshot[eventName] = normalizeNonNegativeInteger(value) ?? 0;
      }
    });
  } catch {
    return null;
  }

  return snapshot;
}

function buildConnectionSnapshot(navigatorObject) {
  const connection = navigatorObject?.connection
    || navigatorObject?.mozConnection
    || navigatorObject?.webkitConnection;
  if (!connection || typeof connection !== "object") {
    return null;
  }

  return {
    downlink_mbps: normalizeFiniteNumber(connection.downlink),
    effective_type: normalizeOptionalText(connection.effectiveType),
    rtt_ms: normalizeFiniteNumber(connection.rtt),
    save_data: connection.saveData === true,
  };
}

function resolveGlobalWindow(windowObject) {
  if (windowObject && typeof windowObject === "object") {
    return windowObject;
  }

  return typeof window === "object" && window ? window : null;
}

export function collectBrowserPerformanceMetrics({
  capturedAt = new Date().toISOString(),
  reportFetchDurationMs = null,
  source = "current_browser",
  windowObject,
} = {}) {
  const resolvedWindow = resolveGlobalWindow(windowObject);
  const documentObject = resolvedWindow?.document ?? null;
  const navigatorObject = resolvedWindow?.navigator ?? null;
  const performanceObject = resolvedWindow?.performance ?? null;
  const screenObject = resolvedWindow?.screen ?? null;
  const tabUptimeMs = typeof performanceObject?.now === "function"
    ? normalizeFiniteNumber(performanceObject.now())
    : null;

  return {
    captured_at: capturedAt,
    source: normalizeOptionalText(source) || "current_browser",
    connection: buildConnectionSnapshot(navigatorObject),
    document: {
      element_count: normalizeNonNegativeInteger(documentObject?.getElementsByTagName?.("*")?.length),
      visibility_state: normalizeOptionalText(documentObject?.visibilityState),
    },
    event_counts: buildEventCountSnapshot(performanceObject),
    long_tasks: buildLongTaskSnapshot(performanceObject),
    memory: buildPerformanceMemorySnapshot(performanceObject),
    navigation: buildNavigationTimingSnapshot(performanceObject),
    navigator: {
      device_memory_gib: normalizeFiniteNumber(navigatorObject?.deviceMemory),
      hardware_concurrency: normalizeNonNegativeInteger(navigatorObject?.hardwareConcurrency),
      language: normalizeOptionalText(navigatorObject?.language),
      max_touch_points: normalizeNonNegativeInteger(navigatorObject?.maxTouchPoints),
      online: typeof navigatorObject?.onLine === "boolean" ? navigatorObject.onLine : null,
      platform: normalizeOptionalText(navigatorObject?.platform),
      user_agent: normalizeOptionalText(navigatorObject?.userAgent),
    },
    paints: buildPaintTimingSnapshot(performanceObject),
    performance: {
      now_ms: tabUptimeMs,
      time_origin_ms: normalizeFiniteNumber(performanceObject?.timeOrigin),
    },
    report_fetch_duration_ms: normalizeFiniteNumber(reportFetchDurationMs),
    resources: buildResourceTimingSnapshot(performanceObject),
    tab_uptime_ms: tabUptimeMs,
    viewport: {
      color_depth: normalizeNonNegativeInteger(screenObject?.colorDepth),
      device_pixel_ratio: normalizeFiniteNumber(resolvedWindow?.devicePixelRatio),
      height_css_px: normalizeNonNegativeInteger(resolvedWindow?.innerHeight),
      screen_height_px: normalizeNonNegativeInteger(screenObject?.height),
      screen_width_px: normalizeNonNegativeInteger(screenObject?.width),
      width_css_px: normalizeNonNegativeInteger(resolvedWindow?.innerWidth),
    },
  };
}

export function buildPerformanceReport(serverReport, options = {}) {
  const normalizedServerReport = serverReport && typeof serverReport === "object" && !Array.isArray(serverReport)
    ? serverReport
    : { server_response: serverReport ?? null };

  return {
    ...normalizedServerReport,
    browser: collectBrowserPerformanceMetrics(options),
  };
}

export function buildRailwayPerformanceReport(bridgePayload, options = {}) {
  const payload = bridgePayload && typeof bridgePayload === "object" && !Array.isArray(bridgePayload)
    ? bridgePayload
    : {};
  const serverReport = payload.report && typeof payload.report === "object" && !Array.isArray(payload.report)
    ? payload.report
    : { server_response: payload.report ?? null };
  const { report: _report, ...bridgeMetadata } = payload;

  return {
    ...serverReport,
    railway_bridge: bridgeMetadata,
    browser: collectBrowserPerformanceMetrics({
      ...options,
      source: "local_emergency_console",
    }),
  };
}

export function serializePerformanceReport(report) {
  return JSON.stringify(report ?? null, null, 2);
}

export function buildPerformanceReportFileName(report) {
  const generatedAt = normalizeOptionalText(report?.generated_at) || new Date().toISOString();
  const safeTimestamp = generatedAt.replace(/[^0-9A-Za-z]+/g, "-").replace(/^-|-$/g, "");
  const prefix = report?.railway_bridge?.target === "railway_production"
    ? "transia-railway-performance"
    : "transia-performance";
  return `${prefix}-${safeTimestamp || "report"}.json`;
}

export function downloadPerformanceReport(report, {
  documentObject = typeof document === "object" ? document : null,
  urlObject = typeof URL === "function" ? URL : null,
} = {}) {
  if (
    !documentObject
    || typeof documentObject.createElement !== "function"
    || !urlObject
    || typeof urlObject.createObjectURL !== "function"
  ) {
    throw new Error("Téléchargement indisponible dans ce navigateur.");
  }

  const fileName = buildPerformanceReportFileName(report);
  const objectUrl = urlObject.createObjectURL(new Blob(
    [serializePerformanceReport(report)],
    { type: "application/json;charset=utf-8" },
  ));
  const anchor = documentObject.createElement("a");

  try {
    anchor.download = fileName;
    anchor.href = objectUrl;
    anchor.hidden = true;
    documentObject.body?.appendChild?.(anchor);
    anchor.click();
  } finally {
    anchor.remove?.();
    urlObject.revokeObjectURL?.(objectUrl);
  }

  return fileName;
}
